/**
 * @file JobsApi.js
 * @fileoverview The job-manager wire protocol: request/response type enums,
 * request construction, and the API functions called by the jobs redux thunks.
 * (Consolidated here from the former `jobs/jobs_protocol.js`.)
 *
 * @typedef {import('./JobDefinition').JobStatusOptions} JobStatusOptions
 * @typedef {import('./JobDefinition').JobTypeOptions} JobTypeOptions
 */

import { hasLogin } from 'BMapsSrc/server/session_request';
import { WebServices } from 'BMapsSrc/WebServices';

/**
 * @typedef {'JobRequest' | 'RetrieveJobResults' | 'DeleteJob'} JobManagerRequestTypeOptions
 * @type {{
 *    JOB_REQUEST: "JobRequest",
 *    RETRIEVE_JOB_RESULTS: "RetrieveJobResults",
 *    DELETE_JOB: "DeleteJob"
 * }}
 */
export const JobManagerRequestTypes = {
    JOB_REQUEST: 'JobRequest',
    RETRIEVE_JOB_RESULTS: 'RetrieveJobResults',
    DELETE_JOB: 'DeleteJob',
    RETRIEVE_JOB_FILE: 'RetrieveJobFile',
};

/**
 * @typedef {'JobCreated' | 'JobResults' | 'JobDeleted'} JobManagerResponseTypeOptions
 * @type {{
 *    JOB_CREATED: "JobCreated",
 *    JOB_RESULTS: "JobResults",
 *    JOB_DELETED: "JobDeleted"
 * }}
 */
export const JobManagerResponseTypes = {
    JOB_CREATED: 'JobCreated',
    JOB_RESULTS: 'JobResults',
    JOB_DELETED: 'JobDeleted',
};

/**
 * Wraps a request payload in the job-manager `JobRequest` envelope.
 * @param {{ jobtype: JobTypeOptions, jobrequest: object, stash?: object,
 *   requestmeta: object }} args
 * @returns {{ type: "JobRequest", jobtype: JobTypeOptions, jobrequest: object,
 *   stash: object, requestmeta: string }}
 */
export function makeJobRequest({
    jobtype, jobrequest, stash={}, requestmeta,
}) {
    return {
        type: JobManagerRequestTypes.JOB_REQUEST,
        jobtype,
        jobrequest,
        stash,
        requestmeta: JSON.stringify(requestmeta),
    };
}

/**
 * Shape of each entry in the get-jobs response, as formed by the receptionist
 * (`services/jobman/src/rsite/receptionist` `doGetJobs`/`precisJob`, see the
 * `Job` dataclass in `receptionist/data.py`).
 *
 * NOTE: get-jobs intentionally does NOT include `stash` or `jobrequest`.
 * Those are only returned by retrieve-job-results (the `JobResults` dataclass)
 * and surface via `loadJobResult` / a job's `localJobState`, never on the
 * `jobs[]` redux entries.
 *
 * `done` and `delivered` are `null` until the job finishes / its results are
 * delivered, then become ISO-timestamp strings (set once, monotonic).
 *
 * @typedef {{
 *    jobid: string,
 *    jobtype: JobTypeOptions,
 *    state: JobStatusOptions,
 *    requestmeta: object,
 *    type: "Job",
 *    created: string,
 *    modified: string,
 *    done: string | null,
 *    delivered: string | null
 * }[]} getJobsResponse
 *
 * @returns {Promise<{ results: getJobsResponse, error: string }>}
 */
export async function getJobs() {
    let results = null;
    let error = '';
    if (await hasLogin()) {
        try {
            const url = `${WebServices.JOBS_URL}get-jobs`;
            const data = await WebServices.startWsRequest(url);
            results = JSON.parse(data);
            for (const job of results) {
                if (job.requestmeta) job.requestmeta = parseRequestMeta(job.requestmeta);
            }
        } catch (errorCaught) {
            console.warn(errorCaught);
            error = 'Error getting jobs list.';
        }
    } else {
        error = 'Jobs requires a BMaps login.';
    }
    return { results, error };
}

/**
 * @typedef {{
 *  type: "JobCreated",
 *  jobid: string,
 *  jobtype: JobTypeOptions,
 *  state: "starting",
 *  requestmeta: object,
 *  created: string,
 * }} postJobResponse
 *
 * @returns {Promise<{ results: postJobResponse, error: string }>}
 */
export async function postJob(data=null) {
    let results = null;
    let error = '';

    if (await hasLogin()) {
        try {
            const url = `${WebServices.JOBS_URL}start-job`;
            results = await WebServices.startWsRequest(url, {
                postData: JSON.stringify(data),
                contentType: 'application/json',
            });
            results = JSON.parse(results);
            if (results.requestmeta) {
                results.requestmeta = parseRequestMeta(results.requestmeta);
            }
            if (results.error) {
                if (results.error.report) {
                    error = results.error.report;
                    console.warn(`Received job error: ${error}`);
                }
                if (results.error.detail) {
                    console.warn(`Received job error detail: ${results.error.detail}`);
                }
            }
        } catch (errorCaught) {
            console.warn(errorCaught);
            error = 'Error starting job.';
        }
    } else {
        error = 'Jobs requires a BMaps login.';
    }
    return { results, error };
}

/**
 *
 * @typedef {{
 * type: "JobDeleted",
 * success: boolean,
 * }} deleteJobResponse
 *
 * @returns {Promise<{ results: deleteJobResponse, error: string }>}
 */
export async function deleteJob(jobid) {
    let results = null;
    let error = '';
    if (await hasLogin()) {
        try {
            const url = `${WebServices.JOBS_URL}delete-job`;
            const data = await WebServices.startWsRequest(url, {
                postData: JSON.stringify({ jobid, type: JobManagerRequestTypes.DELETE_JOB }),
                contentType: 'application/json',
            });
            results = JSON.parse(data);
        } catch (errorCaught) {
            console.warn(errorCaught);
            error = 'Error deleting job.';
        }
    } else {
        error = 'Jobs requires a BMaps login.';
    }
    return { results, error };
}

// export async function cancelJob(userid, jobid) {
//     let results = null;
//     let error = '';
//     if (await hasLogin()) {
//         try {
//             const url = `${WebServices.JOBS_URL}/command?userid=${userid}`;
//             const formData = new FormData();
//             formData.append('userid', userid);
//             formData.append('jobid', jobid);
//             formData.append('op', 'cancel-job');
//             const data = await WebServices.startWsRequest(url, {
// postData: formData });
//             results = JSON.parse(data);
//         } catch (errorCaught) {
//             error = errorCaught;
//         }
//     } else {
//         error = 'Jobs requires a BMaps login.';
//     }
//     return { results, error };
// }

/**
 *
 * @typedef {{
 * jobid: string,
 * state: JobStatusOptions,
 * jobrequest: object,
 * stash: object,
 * jobresult: object,
 * }} loadJobResponse
 *
 * @returns {Promise<{ results: loadJobResponse, error: string }>}
 */
export async function loadJobResult(jobid) {
    let results = null;
    let error = '';
    if (await hasLogin()) {
        try {
            const url = `${WebServices.JOBS_URL}retrieve-job-results`;
            const postData = JSON.stringify(
                { jobid, type: JobManagerRequestTypes.RETRIEVE_JOB_RESULTS }
            );
            const data = await WebServices.startWsRequest(url, { postData });
            const response = JSON.parse(data);
            if (response.jobresult) {
                results = {
                    jobResults: response.jobresult,
                    jobrequest: response.jobrequest,
                    stash: response.stash,
                };
            }
            if (response.state) {
                // Requesting job results may reveal that a "successful" job actually failed.
                // Pass along the state so the caller can update the local job state if needed.
                results = { ...results, state: response.state };
            }
            if (response.error) {
                if (response.error.report) {
                    error = response.error.report;
                    console.warn(`Received job error: ${error}`);
                }
                if (response.error.detail) {
                    console.warn(`Received job error detail: ${response.error.detail}`);
                }
            }
        } catch (errorCaught) {
            console.warn(errorCaught);
            error = 'Error retrieving job results.';
        }
    } else {
        error = 'Jobs requires a BMaps login.';
    }
    return { results, error };
}

/**
 * Fetches one of a job's staged files via the receptionist's generic
 * retrieve-job-file endpoint (`services/jobman/src/rsite/receptionist`'s
 * `reqRetrieveJobFile`) - the browser-facing sibling to `datapark.incrPark`,
 * serving any named file a worker delivered as an extra part alongside a
 * job's `data`/`debug` (see `deliverDataToStaging`'s `extraFiles`). Any job
 * type whose worker stages extra files this way can use this to pull one
 * back on demand, not just automatically at job-completion time.
 *
 * POSTs the {type, jobid, name, member?} JSON-body envelope every other
 * typed request in this file uses (matching `unwrapRequest`'s POST-only
 * convention on the receptionist side), even though the response itself is
 * raw binary, not JSON - postData and responseType are independent options
 * on startWsRequest, so combining a JSON request with an arraybuffer
 * response is well-supported, not a special case.
 *
 * Unlike this file's other functions, throws instead of returning
 * `{results, error}` - a caller fetching several files at once (e.g. one
 * per active compound) needs to catch and report failures per file, not
 * have them swallowed here.
 * @param {string} jobid
 * @param {{ name: string, member?: string }} args `name` selects the staged
 *   file (e.g. "structures.zip"); `member`, if given, extracts one entry
 *   from it as a zip (a central-directory seek, not a full decompress)
 *   instead of returning the whole file.
 * @returns {Promise<ArrayBuffer>}
 */
export async function retrieveJobFile(jobid, { name, member } = {}) {
    const url = `${WebServices.JOBS_URL}retrieve-job-file`;
    return WebServices.startWsRequest(url, {
        postData: JSON.stringify({
            jobid, name, member, type: JobManagerRequestTypes.RETRIEVE_JOB_FILE,
        }),
        contentType: 'application/json',
        responseType: 'arraybuffer',
    });
}

function parseRequestMeta(requestmeta) {
    if (typeof requestmeta === 'string') {
        try {
            return JSON.parse(requestmeta);
        } catch (errorCaught) {
            console.warn(errorCaught);
            return {};
        }
    }
    return requestmeta;
}
